import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export function CajaClosingModal({ isOpen, onClose, onConfirm, currentBalances }) {
  const [closingBalances, setClosingBalances] = useState({
    cash: '',
    transfer: '',
    credit: '',
    other: ''
  });
  const [discrepancies, setDiscrepancies] = useState({
    cash: 0,
    transfer: 0,
    credit: 0,
    other: 0
  });
  const [notes, setNotes] = useState('');

  // Update discrepancies when balance changes
  useEffect(() => {
    if (!currentBalances) return;
    
    // Calculate discrepancies
    const newDiscrepancies = {
      cash: Number(closingBalances.cash || 0) - (currentBalances.cash || 0),
      transfer: Number(closingBalances.transfer || 0) - (currentBalances.transfer || 0),
      credit: Number(closingBalances.credit || 0) - (currentBalances.credit || 0),
      other: Number(closingBalances.other || 0) - (currentBalances.other || 0)
    };
    
    setDiscrepancies(newDiscrepancies);
  }, [closingBalances, currentBalances]);

  const handleChange = (field, value) => {
    setClosingBalances({
      ...closingBalances,
      [field]: value
    });
  };

  const handleConfirm = () => {
    // Validar y convertir valores
    const formattedBalances = {
      cash: Number(closingBalances.cash || 0),
      transfer: Number(closingBalances.transfer || 0),
      credit: Number(closingBalances.credit || 0),
      other: Number(closingBalances.other || 0)
    };
    
    onConfirm({
      balances: formattedBalances,
      discrepancies,
      notes: notes.trim() || null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cerrar Caja</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        <div className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Ingrese los saldos de cierre por método de pago
          </p>
          
          <div className="space-y-4">
            {/* Efectivo */}
            <div>
              <div className="flex justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Efectivo
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Esperado: {currentBalances?.cash || 0}
                </span>
              </div>
              <input
                type="number"
                value={closingBalances.cash}
                onChange={(e) => handleChange('cash', e.target.value)}
                placeholder="0.00"
                className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {discrepancies.cash !== 0 && (
                <p className={`text-xs mt-1 ${discrepancies.cash > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {discrepancies.cash > 0 ? 'Sobrante' : 'Faltante'}: {Math.abs(discrepancies.cash)}
                </p>
              )}
            </div>
            
            {/* Transferencias */}
            <div>
              <div className="flex justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Transferencias
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Esperado: {currentBalances?.transfer || 0}
                </span>
              </div>
              <input
                type="number"
                value={closingBalances.transfer}
                onChange={(e) => handleChange('transfer', e.target.value)}
                placeholder="0.00"
                className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {discrepancies.transfer !== 0 && (
                <p className={`text-xs mt-1 ${discrepancies.transfer > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {discrepancies.transfer > 0 ? 'Sobrante' : 'Faltante'}: {Math.abs(discrepancies.transfer)}
                </p>
              )}
            </div>
            
            {/* Observaciones */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Observaciones (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Detalles adicionales, razón de discrepancias..."
                className="w-full p-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={3}
              />
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancelar
          </button>
          <button 
            onClick={handleConfirm}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
          >
            Cerrar Caja
          </button>
        </div>
      </div>
    </div>
  );
} 